import {
  IconButton,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  backgroundThemes,
  BasePageQuotation,
  Footer,
  HeaderWithPercentBar,
  themeMethods,
} from 'design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Data layer
import {
  appliesAutoPxDiscount,
  isInspectionRequired,
} from 'data-layer-cr/dist/functions/quotations';
import { ProductConfiguration, Quotation } from 'data-layer-cr/dist/models';

import {
  DEDUCTIBLE_OPTION_STEP,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';
import getProgressPercent from 'src/functions/getProgressPercent';

// resources
import { debounce } from 'lodash';
import { ArrowLeft } from 'react-feather';

import { setAutoPx, setPackageOption } from '../../../store/quotation/actions';
import { RootState } from '../../../store/rootReducer';

// Components
import {
  CollectiveSelection,
  ExecutiveButton,
  IndividualSelection,
} from './components';

// Icons
import UnitSelectionIcon from '../../Icons/UnitSelectionIcon';

// Contast
import { setTotalPayments } from '../../../store/emissionPolicy/actions';
import { TotalsPayload } from '../../../store/emissionPolicy/slice';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'auto',
    background: `linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%), 
        ${themeMethods.getCurrentMonthTheme(
          new Date(),
          backgroundThemes.QUOTATION_PAGE,
        )}`,
    [theme.breakpoints.down('sm')]: {
      background: 'none',
    },
  },
  IconButtonGoback: {
    width: 'max-content',
  },
}));

export const UnitSelection = ({
  quotation,
  onNextRequested,
  setPackageOptionAction,
  setAutoPxAction,
  productConfiguration,
  onGoToEmissionWelcome,
  setTotalPaymentsAction,
}: Props): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const classes = useStyles();
  const appliesAutopx = appliesAutoPxDiscount(quotation);
  const [triggerNext, setTriggerNext] = useState(false);

  const [selectedPackage, setSelectedPackage] = useState(
    quotation.packageOption,
  );

  const onNextPressed = useCallback((): void => {
    setPackageOptionAction(selectedPackage);
    trackEvent({
      eventName: DEDUCTIBLE_OPTION_STEP,
      eventProperties: {},
    });
    trackCurrentStep(DEDUCTIBLE_OPTION_STEP);
    onNextRequested();
  }, [selectedPackage, setPackageOptionAction, onNextRequested]);

  /**
   * As well as the payments, the cards can also be spammed
   * so it needs to be invoked with a delay too.
   */
  const debouncedUpdateSelected = useRef(
    debounce<(totals: TotalsPayload) => void>((totals) => {
      setTotalPaymentsAction(totals);
    }, 500),
  ).current;

  useEffect(() => {
    if (triggerNext) {
      onNextPressed();
    }
  }, [triggerNext, onNextPressed]);

  const onUnitSelected = useCallback(
    (packageId: number, forceNext: boolean): void => {
      setSelectedPackage(packageId);
      setTriggerNext(forceNext);
      debouncedUpdateSelected({
        paymentTotal: quotation.units[0].total.grandTotal,
        electronicPaymentTotal: 0,
        autoPxPaymentTotal: 0,
      });
    },
    [debouncedUpdateSelected, quotation.units],
  );
  const setAutoPxValue = useCallback((): void => {
    setAutoPxAction(appliesAutopx);
  }, [setAutoPxAction, appliesAutopx]);

  useMemo(() => setAutoPxValue(), [setAutoPxValue]);

  const backButton = useMemo(
    () => (
      <IconButton
        data-testid="close-button"
        onClick={onGoToEmissionWelcome}
        className={classes.IconButtonGoback}
      >
        <ArrowLeft />
      </IconButton>
    ),
    [onGoToEmissionWelcome, classes.IconButtonGoback],
  );

  const inspectionRequired = useMemo(
    () =>
      isInspectionRequired(quotation, productConfiguration.inspectionRequired),
    [quotation, productConfiguration],
  );

  const currentStep = useMemo(() => 1, []);
  const totalSteps = useMemo(
    () => (inspectionRequired ? 5 : 6),
    [inspectionRequired],
  );

  const isCollectiveQuotation = useMemo(
    () => quotation.quotationProfile.type === 'colectiva',
    [quotation],
  );

  return (
    <>
      <div className={classes.container}>
        <BasePageQuotation
          data-testid="header"
          rightActions={null}
          leftActions={backButton}
          backgroundColor="white"
          stepperItem={
            <HeaderWithPercentBar
              data-testid="stepper"
              goToBackPage={onGoToEmissionWelcome}
              icon={<UnitSelectionIcon />}
              stepperTitle="Opciones de coberturas"
              currentProgress={getProgressPercent(currentStep, totalSteps)}
            />
          }
        >
          {isCollectiveQuotation && (
            <CollectiveSelection
              data-testid="collective-selection"
              onSelected={onUnitSelected}
            />
          )}
          {!isCollectiveQuotation && (
            <IndividualSelection
              data-testid="individual-selection"
              quotation={quotation}
              onUnitSelected={onUnitSelected}
            />
          )}
          <ExecutiveButton data-testid="executive-button" />
        </BasePageQuotation>
        <Footer
          IsMobile={isMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </div>
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  productConfiguration: ProductConfiguration;
} => ({
  quotation: state.quotation.quotation,
  productConfiguration: state.coverageCatalog.productConfig,
});

const reduxActions = {
  setPackageOptionAction: setPackageOption,
  setAutoPxAction: setAutoPx,
  setTotalPaymentsAction: setTotalPayments,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onNextRequested: () => void;
  onGoToEmissionWelcome: () => void;
}

export default connector(UnitSelection);
